import { BACKEND_URL } from '../config/backendUrl';
/* usePlan — tiny client-side plan-feature gate.
 *
 * Reads from `GET /api/usage/me` once (memoised at provider level via
 * React Query? No — we don't have RQ here, so simple useState + cache).
 * Exposes:
 *   const { plan, usage, limits, hasFeature, loading } = usePlan();
 *
 * Feature keys (from PLAN_CATALOG.features in backend/routes/plans.py):
 *   - online_forms
 *   - ai_form_generator
 *   - pdf_templates
 *   - esign
 *   - bullhorn_basic / bullhorn_full
 *   - email_reminders
 *   - packets
 *   - audit_certs
 *   - batch_send / bulk_send
 *   - credentials / credential_requests / renewal_reminders
 *   - tenant_clone
 *
 * The hook deliberately fails-open when the API errors or the user is a
 * Master Admin — feature gating is a UX convenience, not a security
 * boundary (server-side enforcement is where real checks live).
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API_URL = `${BACKEND_URL}/api`;

// In-memory cache — usage data doesn't need to be refetched on every
// hook mount; we keep it for the duration of the tab. Components that
// want a hard refresh can call `refresh()` from the returned object.
let _cache = null;
let _inflight = null;

async function fetchUsage() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = axios.get(`${API_URL}/usage/me`)
    .then(r => { _cache = r.data; _inflight = null; return _cache; })
    .catch(err => {
      _inflight = null;
      // Fail-open: a missing API shouldn't break the whole UI.
      console.warn('usePlan: usage fetch failed:', err?.response?.status);
      _cache = {
        plan: { plan_id: 'documents', plan_name: 'Pro', features: ['online_forms', 'pdf_templates', 'esign', 'bullhorn_basic', 'packets', 'audit_certs', 'email_reminders'] },
        usage: { sends: 0, templates: 0 },
        limits: { send_limit: -1, template_limit: -1, seats: -1 },
      };
      return _cache;
    });
  return _inflight;
}

export function refreshPlanCache() {
  _cache = null;
  _inflight = null;
}

export function usePlan() {
  const [data, setData] = useState(_cache);
  const [loading, setLoading] = useState(!_cache);

  useEffect(() => {
    if (_cache) return undefined;
    let mounted = true;
    fetchUsage().then(d => {
      if (!mounted) return;
      setData(d);
      setLoading(false);
    });
    return () => { mounted = false; };
  }, []);

  const refresh = useCallback(async () => {
    refreshPlanCache();
    const fresh = await fetchUsage();
    setData(fresh);
    return fresh;
  }, []);

  const features = data?.plan?.features || [];
  const hasFeature = useCallback((key) => {
    if (!data) return true; // fail-open while loading
    if (features.includes('all')) return true;
    return features.includes(key);
  }, [data, features]);

  return {
    loading,
    plan: data?.plan || null,
    usage: data?.usage || { sends: 0, templates: 0 },
    limits: data?.limits || { send_limit: -1, template_limit: -1, seats: -1 },
    hasFeature,
    refresh,
  };
}
